import { apiClient } from './api';

export type SkuStatus = 'active' | 'inactive' | 'discontinued';

export interface Sku {
  id: number;
  sku_code: string;
  barcode: string | null;
  product_name: string;
  description: string | null;
  category_id: number | null;
  category_name: string | null;
  unit_cost: number;
  list_price: number | null;
  /**
   * VP-CUST-002: customer-tier-adjusted price, present when the caller is a
   * Customer (backend resolves via the caller's pricing_tier discount_factor).
   * Equal to list_price when there's no tier discount, or absent entirely
   * for non-Customer callers.
   */
  customer_price?: number | null;
  age_restriction: '18+' | '21+' | null;
  status: SkuStatus;
  image_url: string | null;
  reorder_point: number;
  reorder_qty: number;
  total_stock: number;
  margin_percent: number;
  created_at: string;
  updated_at: string;
}

export interface ListSkusFilters {
  page?: number;
  limit?: number;
  search?: string;
  category_id?: number;
  status?: SkuStatus;
  in_stock_only?: boolean;
}

export interface SkuInput {
  sku_code: string;
  barcode?: string | null;
  product_name: string;
  description?: string;
  category_id?: number | null;
  unit_cost: number;
  list_price?: number | null;
  age_restriction?: '18+' | '21+' | null;
  status?: SkuStatus;
  image_url?: string | null;
  reorder_point?: number;
  reorder_qty?: number;
}

export interface PaginatedSkus {
  data: Sku[];
  pagination: { total: number; page: number; limit: number; pages: number };
}

export async function listSkus(filters: ListSkusFilters = {}): Promise<PaginatedSkus> {
  return apiClient.get<PaginatedSkus>('/skus', filters as Record<string, any>);
}

export async function getSku(id: number | string): Promise<Sku> {
  return apiClient.get<Sku>(`/skus/${id}`);
}

/**
 * Resolve a scanned barcode (or sku_code fallback) to a product for the POS.
 * Throws on 404 (caller catches to show a "not found" message).
 */
export async function lookupSkuByBarcode(barcode: string): Promise<Sku> {
  return apiClient.get<Sku>('/skus/lookup', { barcode });
}

export async function createSku(input: SkuInput): Promise<Sku> {
  return apiClient.post<Sku>('/skus', input);
}

export async function updateSku(id: number | string, input: Partial<SkuInput>): Promise<Sku> {
  return apiClient.patch<Sku>(`/skus/${id}`, input);
}

export async function deleteSku(id: number | string): Promise<void> {
  await apiClient.delete<void>(`/skus/${id}`);
}
